import ReactDOM from 'react-dom';
import React, { Fragment, CSSProperties, useState, useEffect } from 'react';
import './style.scss';
import FitSlider from './FitSlider';
import { useMediaQuery } from '@material-ui/core';
import { MUI_MOBILE_BREAKPOINT } from '../../../../utils/constants';
import { aloApiFetch } from 'src/scripts/utils/alo-api-fetch';
import ReviewsSummaryTooltip from './ReviewsSummaryTooltip';
import { SiteSettings } from 'src/scripts/utils/environment';
import { pluralize } from 'src/scripts/utils/helpers';

const isCustomReviewsHeaderEnabled = SiteSettings?.customReviewsHeaderEnabled;
const isBazaarvoiceReviewHiglightsEnabled = SiteSettings?.bazaarvoiceReviewHighlightsEnabled;
const aloReviewHighlights = window.server_pdp_data?.metaFields.aloReviews['review-highlights'];

const getBVreviewData = async (handle: string | undefined) => {
  const response = await aloApiFetch(`/v1/review/product_summary?productHandle=${handle}`);
  if (!response.ok) {
    throw new Error('Error fetching reviews');
  }
  return response.json();
};

//  ~ summary
export const ReviewsBottomLine = ({
  colorwayProductId,
  isStandaloneVersion = false,
  productHandle,
}: {
  colorwayProductId?: string;
  isStandaloneVersion?: boolean;
  productHandle?: string;
}) => {
  const [bvReviewsData, setBVReviewsData] = useState<ProductReviews | undefined>();
  useEffect(() => {
    try {
      productHandle &&
        getBVreviewData(productHandle)
          .then((data) => {
            setBVReviewsData(data);
            renderAloAIReviewSummary();
            renderBVReviewsHeader(data);
          })
          .catch((err) => {
            console.log(err);
          });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [colorwayProductId, productHandle]);

  const averageScore = bvReviewsData?.rating;

  const totalReviews = bvReviewsData?.total_reviews;

  if (!bvReviewsData) return null;

  if ((totalReviews ?? 0) <= 0) return null;

  return (
    <Fragment>
      <span className={`product__price h6 uppercase ${isStandaloneVersion ? 'pdp-summary-styles' : ''}`}>
        {isStandaloneVersion ? 'Reviews ' : '|'}
      </span>
      <a
        href="#reviews-section-anchor"
        aria-label="Go to reviews"
        className="ReviewsBottomLine"
        style={isStandaloneVersion ? { padding: '10px 8px 0' } : {}}
      >
        <StarsUI score={averageScore as number} starSize="20px" color="var(--highlight-medium)" />{' '}
        <div style={isStandaloneVersion ? { color: '#000000' } : {}}>({totalReviews})</div>
      </a>
      <ReviewsSummaryTooltip reviewsData={bvReviewsData} />
    </Fragment>
  );
};

export const BVReviewsHeader = ({
  reviewsData,
  isSecondary = false,
}: {
  reviewsData?: ProductReviews;
  isSecondary?: boolean;
}) => {
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });
  if (
    !reviewsData ||
    reviewsData.rating == null ||
    reviewsData.total_reviews == null ||
    reviewsData.total_reviews <= 0 ||
    (isSecondary && (!reviewsData.fit?.rating || !reviewsData.summary || !aloReviewHighlights))
  ) {
    return null;
  }

  return (
    <Fragment>
      <div className={`review-container reviews-summary ${isSecondary ? 'secondary' : ''}`} id="reviews-summary">
        {!isSecondary && <h2 className="h3 mb-xl">Reviews</h2>}
        <SummaryWrapper
          score={reviewsData.rating}
          totalReviews={reviewsData.total_reviews}
          isMobile={isMobile}
          isSecondary={isSecondary}
          fitAverageRating={reviewsData.fit.rating}
          fitRatingRange={reviewsData.fit.range}
          fitLabels={reviewsData.fit.labels}
          reviewHighlights={
            isBazaarvoiceReviewHiglightsEnabled ? reviewsData.summary || aloReviewHighlights : aloReviewHighlights
          }
        />
      </div>
    </Fragment>
  );
};

export const AloAIReviewSummary = () => {
  if (!aloReviewHighlights) return null;
  return (
    <div className="alo-ai-review-summary">
      <h2 className="h2">Review Highlights</h2>
      <p className="alo-ai-summary-text">{aloReviewHighlights}</p>
    </div>
  );
};

const SummaryWrapper = ({
  score,
  totalReviews,
  isMobile,
  isSecondary,
  fitAverageRating,
  fitRatingRange,
  fitLabels,
  reviewHighlights,
}: {
  score: number;
  totalReviews: number;
  isMobile: boolean;
  isSecondary: boolean;
  fitAverageRating?: number;
  fitRatingRange?: number;
  reviewHighlights?: string;
  fitLabels?: string[];
}) => (
  <>
    <div className="summary-wrapper row">
      <div className="stars-column col-12 col-md-2">
        <div className="summary-subtitle d-flex justify-content-between">
          {`${pluralize(parseFloat(score.toFixed(1)), 'star')}`}
          {isSecondary && isMobile ? (
            <span className="total-reviews">{`${pluralize(totalReviews, 'review')}`}</span>
          ) : null}
        </div>
        <StarsUI score={score} starSize={isMobile ? '30px' : '27px'} color="var(--highlight-main)" />
        <div className="total-reviews d-none d-md-block">{`${pluralize(totalReviews, 'review')}`}</div>
      </div>
      {fitAverageRating && totalReviews > 0 ? (
        <FitSummarySlider fitAverageRating={fitAverageRating} fitRatingRange={fitRatingRange} fitLabels={fitLabels} />
      ) : null}
      {reviewHighlights ? (
        <div className="reviews-highlight col-12 col-xl-6 col-md-4">
          <div className="summary-subtitle">Review Highlights</div>
          <p className="summary-text">{reviewHighlights}</p>
        </div>
      ) : null}
    </div>
    {isSecondary && (
      <div className="row">
        <div className="col-6 offset-0 col-md-3 col-xl-2 offset-md-9 offset-xl-10 anchor-wrapper">
          <a href="#reviews-section-anchor" className="btn-anchor">
            VIEW ALL REVIEWS
          </a>
        </div>
      </div>
    )}
  </>
);

export const FitSummarySlider = ({
  fitAverageRating,
  fitRatingRange = 3,
  fitLabels = ['Runs small', 'Runs large'],
  fullWidth,
}: {
  fitAverageRating: number;
  fitRatingRange?: number;
  fitLabels?: string[];
  fullWidth?: boolean;
}) => {
  return (
    <div className={`fit-summary col-12 ${fullWidth ? '' : 'col-xl-4 col-md-6'}`}>
      <div className="summary-subtitle d-flex align-items-center" style={{ gap: 8 }}>
        Size & Fit Average{' '}
      </div>
      <FitSlider averageRating={fitAverageRating} fitRange={fitRatingRange} fitLabels={fitLabels} />
    </div>
  );
};

export const StarsUI = ({
  score,
  starSize,
  color = 'var(--primary)',
}: {
  score: number;
  starSize: string;
  color?: string;
}) => {
  return (
    <div className="StarsUI">
      <div
        style={{ '--rating': score, '--starSize': starSize, '--starBackground': color } as React.CSSProperties}
        className="review-star"
        aria-label={`Rating of this product is ${score} out of 5.`}
      />
    </div>
  );
};

const renderAloAIReviewSummary = () => {
  const mainMountNode = document.querySelector('[react-render-target="reviews-alo-ai-summary"]');
  if (mainMountNode) ReactDOM.render(<AloAIReviewSummary />, mainMountNode);
  else console.warn('missing render target: reviews-alo-ai-summary');
};

const renderBVReviewsHeader = (data?: ProductReviews | undefined) => {
  const mainMountNode = document.querySelector('[react-render-target="reviews-header"]');
  if (mainMountNode && isCustomReviewsHeaderEnabled)
    ReactDOM.render(<BVReviewsHeader reviewsData={data} />, mainMountNode);
  else console.warn('missing render target: reviews-summary');
};

export interface MutatedCustomFields {
  type: string;
  data: {
    value: number;
    title: string;
    form_id: number;
    field_type: string;
  };
}

export interface User {
  user_id: number;
  social_image: null;
  user_type: string;
  is_social_connected: number;
  display_name: string;
}

export interface CSSPropertiesWithVars extends CSSProperties {
  '--rating': string;
}

export interface ProductReviews {
  summary: string;
  rating: number;
  total_reviews: number;
  fit: FitDetails;
}

interface FitDetails {
  rating: number;
  range: number;
  labels: string[];
}
